import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Lock, Users, BookOpen, Eye, SignOut } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { AppFrame } from "../components/Layout";
import { useAuth } from "../lib/auth";

const fmtMMSS = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function Admin() {
  const { user, login, logout, authFetch, loading, changePassword } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [lockLeft, setLockLeft] = useState(0);

  useEffect(() => {
    if (lockLeft <= 0) return;
    const id = setInterval(() => setLockLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [lockLeft]);
  const [pwCur, setPwCur] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwMsg, setPwMsg] = useState("");
  const [pwErr, setPwErr] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const submitPw = async (e) => {
    e.preventDefault();
    setPwMsg("");
    setPwErr("");
    if (pwNew.length < 6) {
      setPwErr("Le nouveau mot de passe doit contenir au moins 6 caractères.");
      return;
    }
    setPwBusy(true);
    try {
      await changePassword(pwCur, pwNew);
      setPwMsg("Mot de passe mis à jour avec succès.");
      setPwCur("");
      setPwNew("");
    } catch (e2) {
      setPwErr(e2.message || "Échec de la mise à jour.");
    } finally {
      setPwBusy(false);
    }
  };

  const isAdmin = user && user.role === "admin";

  const loadStats = useCallback(async () => {
    const r = await authFetch("/admin/stats");
    if (r.ok) setStats(await r.json());
  }, [authFetch]);

  useEffect(() => {
    if (isAdmin) loadStats();
  }, [isAdmin, loadStats]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u.role !== "admin") {
        setErr("Ce compte n'a pas les droits d'administration.");
        logout();
      }
    } catch (e2) {
      setErr(e2.message || "Connexion impossible.");
      if (e2.retryAfter) setLockLeft(e2.retryAfter);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <AppFrame>
        <div className="px-6 pt-16 text-center text-[#36302B]/50">Chargement…</div>
      </AppFrame>
    );
  }

  if (!isAdmin) {
    return (
      <AppFrame>
        <div className="px-6 pb-16 pt-10">
          <Link to="/" className="mb-6 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm">
            <ArrowLeft size={20} color="#36302B" />
          </Link>
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#36302B]/8">
            <Lock size={26} color="#36302B" weight="duotone" />
          </div>
          <h1 className="font-display text-3xl font-medium text-[#36302B]">Administration</h1>
          <p className="mt-2 text-[14px] text-[#36302B]/60">Accès réservé — statistiques globales anonymes.</p>

          <form onSubmit={submit} className="mt-6 space-y-3">
            <input
              data-testid="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email administrateur"
              required
              className="w-full rounded-2xl border border-[#36302B]/10 bg-white px-4 py-3.5 text-[15px] outline-none focus:border-[#C57B67]"
            />
            <input
              data-testid="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mot de passe"
              required
              className="w-full rounded-2xl border border-[#36302B]/10 bg-white px-4 py-3.5 text-[15px] outline-none focus:border-[#C57B67]"
            />
            {err && <p data-testid="admin-error" className="text-[13px] font-semibold text-[#B56A63]">{err}</p>}
            {lockLeft > 0 && (
              <p data-testid="admin-lock-countdown" className="text-[13px] font-bold text-[#B56A63]">
                Réessayez dans {fmtMMSS(lockLeft)}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-submit"
              disabled={busy || lockLeft > 0}
              className="w-full rounded-full bg-[#36302B] py-4 text-[15px] font-bold text-white disabled:opacity-70"
            >
              {busy ? "Un instant…" : lockLeft > 0 ? `Bloqué (${fmtMMSS(lockLeft)})` : "Accéder au dashboard"}
            </button>
          </form>
        </div>
      </AppFrame>
    );
  }

  const opens = stats?.opens_by_day || [];
  const top = stats?.top_fiches || [];

  return (
    <AppFrame>
      <div className="px-6 pb-16 pt-10">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl font-medium text-[#36302B]">Dashboard</h1>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="flex items-center gap-1.5 rounded-full border border-[#36302B]/10 bg-white px-3 py-2 text-[13px] font-semibold text-[#36302B]"
          >
            <SignOut size={16} /> Quitter
          </button>
        </div>
        <p className="mt-1 text-[13px] text-[#36302B]/55">Statistiques 100% anonymes — aucune donnée de santé.</p>

        <div className="mt-5 grid grid-cols-3 gap-3">
          <StatCard icon={Eye} label="Ouvertures" value={stats?.total_opens ?? 0} color="#C57B67" />
          <StatCard icon={BookOpen} label="Fiches lues" value={stats?.total_reads ?? 0} color="#9CAEA9" />
          <StatCard icon={Users} label="Comptes" value={stats?.total_users ?? 0} color="#D3B683" />
        </div>

        <h2 className="font-display mt-7 mb-3 text-xl font-medium text-[#36302B]">Ouvertures par jour</h2>
        <div className="rounded-[26px] border border-[#36302B]/6 bg-white p-4" data-testid="admin-opens-chart">
          <div style={{ width: "100%", height: 180 }}>
            <ResponsiveContainer>
              <BarChart data={opens.map((o) => ({ day: o.day.slice(5), count: o.count }))}>
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#36302B99" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#36302B99" }} axisLine={false} tickLine={false} width={28} />
                <Tooltip contentStyle={{ borderRadius: 14, border: "1px solid rgba(54,48,43,0.08)", fontSize: 12 }} />
                <Bar dataKey="count" fill="#C57B67" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <h2 className="font-display mt-7 mb-3 text-xl font-medium text-[#36302B]">Fiches les plus lues</h2>
        <div className="space-y-2" data-testid="admin-top-fiches">
          {top.length === 0 && <p className="text-[14px] text-[#36302B]/50">Aucune lecture enregistrée pour l'instant.</p>}
          {top.map((t, i) => (
            <div key={t.slug} className="flex items-center gap-3 rounded-2xl border border-[#36302B]/6 bg-white px-4 py-3">
              <span className="font-display text-[15px] text-[#36302B]/40">{i + 1}</span>
              <span className="flex-1 text-[14px] font-semibold text-[#36302B]">{t.slug}</span>
              <span className="rounded-full bg-[#C57B67]/12 px-3 py-1 text-[12px] font-bold text-[#C57B67]">{t.count}</span>
            </div>
          ))}
        </div>

        {/* Sécurité */}
        <h2 className="font-display mt-7 mb-3 text-xl font-medium text-[#36302B]">Sécurité</h2>
        <form onSubmit={submitPw} className="rounded-[26px] border border-[#36302B]/6 bg-white p-5" data-testid="admin-security">
          <div className="mb-3 flex items-center gap-2">
            <Lock size={20} color="#36302B" weight="duotone" />
            <p className="font-display text-[16px] font-medium text-[#36302B]">Changer le mot de passe</p>
          </div>
          <input
            data-testid="admin-pw-current"
            type="password"
            value={pwCur}
            onChange={(e) => setPwCur(e.target.value)}
            placeholder="Mot de passe actuel"
            required
            className="mb-2 w-full rounded-2xl border border-[#36302B]/10 bg-[#FAF8F5] px-4 py-3 text-[15px] outline-none focus:border-[#C57B67]"
          />
          <input
            data-testid="admin-pw-new"
            type="password"
            value={pwNew}
            onChange={(e) => setPwNew(e.target.value)}
            placeholder="Nouveau mot de passe (min. 6 caractères)"
            required
            className="mb-2 w-full rounded-2xl border border-[#36302B]/10 bg-[#FAF8F5] px-4 py-3 text-[15px] outline-none focus:border-[#C57B67]"
          />
          {pwErr && <p data-testid="admin-pw-error" className="mb-2 text-[13px] font-semibold text-[#B56A63]">{pwErr}</p>}
          {pwMsg && <p data-testid="admin-pw-success" className="mb-2 text-[13px] font-semibold text-[#88A096]">{pwMsg}</p>}
          <button
            type="submit"
            data-testid="admin-pw-submit"
            disabled={pwBusy}
            className="w-full rounded-full bg-[#36302B] py-3.5 text-[14px] font-bold text-white disabled:opacity-70"
          >
            {pwBusy ? "Un instant…" : "Mettre à jour le mot de passe"}
          </button>
          <p className="mt-3 text-center text-[11.5px] text-[#36302B]/45">
            Pour votre sécurité, vous serez automatiquement déconnectée après 15 minutes d'inactivité.
          </p>
        </form>

        <p className="mt-6 text-center text-[11.5px] leading-relaxed text-[#36302B]/45">
          Conformément au choix de confidentialité, aucune moyenne EPDS n'est calculée : les résultats du test
          restent exclusivement sur l'appareil des utilisatrices.
        </p>
      </div>
    </AppFrame>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-3xl border border-[#36302B]/6 bg-white p-4 text-center">
      <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-2xl" style={{ backgroundColor: `${color}22` }}>
        <Icon size={20} color={color} weight="duotone" />
      </div>
      <p className="font-display text-2xl font-medium text-[#36302B]">{value}</p>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[#36302B]/45">{label}</p>
    </div>
  );
}
